import {
  formattedRate,
  formatDate,
} from "../../../../components/common/Converter";
import { FIELD_FILTER_MAP } from "./constants/payrollConstants";
import { findObjectUseCase } from "../../../../usecases/object";
export function toCamelCase(str) {
  if (!str) return "";
  return str
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .replace(/\b(\w+)\b/g, (match) => match.toLowerCase())
    .replace(/ (\w)/g, (_, letter) => letter.toUpperCase())
    .trim();
}

const NAME_FIELD_KEY_OVERRIDES = {
  lastName: "ln",
  firstName: "fn",
  middleName: "mn",
  sequence: "",
  ratePerDay: "apRateForBackPay",
};

export function headerLabelToFieldKey(label) {
  const key = toCamelCase(label);
  if (Object.prototype.hasOwnProperty.call(NAME_FIELD_KEY_OVERRIDES, key)) {
    return NAME_FIELD_KEY_OVERRIDES[key];
  }
  return key;
}

export function convertLabels(headers = []) {
  if (!Array.isArray(headers)) return [];

  const labelCount = {};

  return headers
    .filter((item) => !(typeof item === "object" && item.colSpan))
    .map((item) => {
      let label = "";

      if (typeof item === "string") {
        label = headerLabelToFieldKey(item);
      } else if (typeof item === "object") {
        if (item.label) {
          label = headerLabelToFieldKey(item.label);
        } else if (item.name) {
          label = headerLabelToFieldKey(item.name);
        }
      }

      if (label) {
        labelCount[label] = (labelCount[label] || 0) + 1;
        if (labelCount[label] > 1) {
          label += labelCount[label];
        }
      }

      return { label };
    });
}

export function convertFlatStringFactory() {
  const labelCount = {};

  return function convertFlatString(header) {
    if (typeof header !== "string") return "";

    let label = headerLabelToFieldKey(header);

    if (label) {
      labelCount[label] = (labelCount[label] || 0) + 1;
      if (labelCount[label] > 1) {
        label += labelCount[label];
      }
      return label;
    }

    return "";
  };
}

export function convertWithGroupIndex(label, groupIndex) {
  const baseKey = label
    .toLowerCase()
    .replace(/[^\w\s]/gi, "") // remove special characters
    .replace(/\s+/g, " ") // collapse multiple spaces
    .trim()
    .replace(/\s(.)/g, (match, group1) => group1.toUpperCase()) // camelCase
    .replace(/\s/g, "")
    .replace(/^(.)/, (match, group1) => group1.toLowerCase());

  return `${baseKey}${groupIndex}`; // append group index
}

export function sumProperties(data, keys, deductions) {
  let effectiveKeys = [...keys];

  if (effectiveKeys.includes("deductionsSet") && deductions) {
    const {
      deductionType = [],
      additionalType = [],
      contributionType = [],
      incentives = [],
    } = deductions;

    const additionalForSp = incentives[0]?.otherAdditionals;
    const deductionForSp = incentives[0]?.otherDeductions;

    const convertedKeys = [
      ...convertLabels(deductionType),
      ...convertLabels(additionalType),
      ...convertLabels(contributionType),
      ...convertLabels(additionalForSp),
      ...convertLabels(deductionForSp),
    ].map((item) => item.label);

    effectiveKeys = effectiveKeys.filter((key) => key !== "deductionsSet");
    effectiveKeys.push(...convertedKeys);
  }

  const totals = Object.fromEntries(effectiveKeys.map((k) => [k, 0]));

  for (const row of data) {
    if (!row || typeof row !== "object") continue;
    for (const key of effectiveKeys) {
      const value = row[key];

      if (Array.isArray(value)) {
        for (const item of value) {
          totals[key] += parseValue(item);
        }
      } else {
        totals[key] += parseValue(value);
      }
    }
  }

  return totals;
}

function parseValue(val) {
  if (typeof val === "number" && !isNaN(val)) return val;

  if (typeof val === "string") {
    // Match numbers with optional minus sign, but only if the minus is right before the number
    const matches = val.match(/-?\d[\d,]*(\.\d+)?/g);

    if (matches && matches.length > 0) {
      const last = matches[matches.length - 1];
      return parseFloat(last.replace(/,/g, ""));
    }
  }

  return 0;
}


export const getDefaultOfDuty = (assignObjectOrItem) => {
  const assignedArea =
    assignObjectOrItem?.assignObject?.assignedArea ??
    assignObjectOrItem?.assignedArea;

  return assignedArea ? 15 : 22;
};

// Parse hours from strings like "10.5 Hours" or "9 Hours".
// Returns fallbackHours (default 12) if missing/invalid.
export const parseTwentyFourSevenHours = (twentyFourSeven, fallbackHours = 12) => {
  if (typeof twentyFourSeven !== "string") return fallbackHours;
  const match = twentyFourSeven.match(/([\d.]+)/);
  const parsed = match ? parseFloat(match[1]) : NaN;
  return Number.isFinite(parsed) ? parsed : fallbackHours;
};

export const getMatchedCsvRowByAgency = (csv, agency) => {
  if (!Array.isArray(csv)) return null;

  const row = csv.find(
    (item) =>
      item?.agencyEmployeeNo &&
      String(item.agencyEmployeeNo) === String(agency)
  );

  if (!row) return null;

  const { name, agencyEmployeeNo, tax, ...filteredRow } = row;
  return filteredRow;
};

export const calculateAmount = (expression, rate) => {
  try {
    // If rate is an array, return an array of calculated amounts
    if (Array.isArray(rate)) {
      return rate.map((r) => {
        const expr = expression.replace(/,/g, "").replace(/monthly_rate/g, r);
        const result = Function(`"use strict"; return (${expr})`)();
        return Math.round(result * 100) / 100;
      });
    }
    
    // Single rate calculation
    expression = expression.replace(/,/g, "").replace(/monthly_rate/g, rate);
    const result = Function(`"use strict"; return (${expression})`)();
    return Math.round(result * 100) / 100;
  } catch (error) {
    console.error("Error evaluating amount:", error);
    return "";
  }
};

export const _calculateAmountRoundDown = (expression, rate) => {
  try {
    // If rate is an array, return an array of calculated amounts
    if (Array.isArray(rate)) {
      return rate.map((r) => {
        const expr = expression.replace(/,/g, "").replace(/monthly_rate/g, r);
        const result = Function(`"use strict"; return (${expr})`)();
        return Math.floor(result * 100) / 100;
      });
    }
    
    // Single rate calculation
    expression = expression.replace(/,/g, "").replace(/monthly_rate/g, rate);
    const result = Function(`"use strict"; return (${expression})`)();

    return Math.floor(result * 100) / 100;
  } catch (error) {
    console.error("Error evaluating amount:", error);
    return "";
  }
};

export const roundDownAmount = (value) => {
  const fixedValue = Math.floor(value * 1000000) / 1000000;
  return parseFloat(Math.floor(fixedValue * 100) / 100);
};

export const groupAndSumByMonth = (values, months) => {
  if (!Array.isArray(months)) {
    return [];
  }

  const map = new Map();

  months.forEach((month, i) => {
    const val = parseFloat(values[i]) || 0;
    map.set(month, (map.get(month) || 0) + val);
  });

  return Array.from(map.entries()).map(([month, value]) => ({
    month,
    value: parseFloat(value.toFixed(2)),
  }));
};

/** Coerce to a finite number; preserves negatives (avoid parseFloat(x) || 0). */
const toNum = (v) => {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (v === null || v === undefined) return 0;
  const s = String(v).replace(/,/g, "");
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};

export const calculateArray = (base, operations, options = {}) => {
  const { allowNegative = false } = options;

  const arrayLength = Array.isArray(base)
    ? base.length
    : operations.find((op) => Array.isArray(op.value))?.value.length || 0;

  if (arrayLength === 0) return [];

  return Array.from({ length: arrayLength }).map((_, index) => {
    const initialValue = Array.isArray(base) ? toNum(base[index]) : toNum(base);

    const finalResult = operations.reduce((result, { type, value }) => {
      const actualValue = Array.isArray(value)
        ? toNum(value[index])
        : value === "item"
        ? index
        : toNum(value);

      switch (type) {
        case "+":
          return result + actualValue;

        case "-":
          if (allowNegative) {
            return result - actualValue;
          }
          return actualValue > result ? 0 : result - actualValue;

        case "*":
          return result * actualValue;

        case "/":
          return actualValue !== 0 ? result / actualValue : result;

        default:
          return result;
      }
    }, initialValue);
    return parseFloat(finalResult.toFixed(2));
  });
};

export const calculate = (initialValue, operations) => {
  // return operations.reduce((result, { type, value }) => {
  //   const parsedValue = parseFloat(value) || 0;

  //   let intermediateResult;
  //   switch (type) {
  //     case "add":
  //       intermediateResult = result + parsedValue;
  //       break;
  //     case "subtract":
  //       intermediateResult = result - parsedValue;
  //       break;
  //     case "multiply":
  //       intermediateResult = result * parsedValue;
  //       break;
  //     case "divide":
  //       intermediateResult = parsedValue !== 0 ? result / parsedValue : result;
  //       break;
  //     default:
  //       intermediateResult = result;
  //   }

  //   return parseFloat(formattedRate(intermediateResult.toString()));
  // }, parseFloat(initialValue) || 0);

  const finalResult = operations.reduce((result, { type, value }) => {
    const parsedValue = parseFloat(value) || 0;

    switch (type) {
      case "add":
        return result + parsedValue;
      case "subtract":
        return result - parsedValue;
      case "multiply":
        return result * parsedValue;
      case "divide":
        return parsedValue !== 0 ? result / parsedValue : result;
      default:
        return result;
    }
  }, parseFloat(initialValue) || 0);

  // Only round once, at the end
  // return parseFloat(formattedRate(finalResult.toString()));
  const rounded = parseFloat(finalResult.toFixed(2));

  // 🧽 Clean up -0 to 0
  return rounded === 0 ? 0 : rounded;
};

export const mergeHeaders = (tableHeaders, primarySubHdr, secondarySubHdr) => {
  const safeTableHeaders = Array.isArray(tableHeaders) ? tableHeaders : [];
  const safePrimarySubHdr = Array.isArray(primarySubHdr) ? primarySubHdr : [];
  const safeSecondarySubHdr = Array.isArray(secondarySubHdr)
    ? secondarySubHdr
    : [];

  let mergedHeaders = [];
  let primaryIndex = 0;
  let secondaryIndex = 0;

  for (let header of safeTableHeaders) {
    if (header.label && !header.rowSpan && !header.colSpan) {
      mergedHeaders.push({ label: header.label });
      continue;
    }

    if (header.rowSpan) {
      mergedHeaders.push(header);
    } else if (header.colSpan) {
      let primaryCount = 0;

      while (
        primaryCount < header.colSpan &&
        primaryIndex < safePrimarySubHdr.length
      ) {
        let subHeader = safePrimarySubHdr[primaryIndex];

        if (subHeader) {
          mergedHeaders.push({ label: subHeader });
          primaryCount++;
        }
        primaryIndex++;
      }

      if (safeSecondarySubHdr.length > 0) {
        for (
          let i = 0;
          i < primaryCount && secondaryIndex < safeSecondarySubHdr.length;
          i++
        ) {
          mergedHeaders.push({ label: safeSecondarySubHdr[secondaryIndex] });
          secondaryIndex++;
        }
      }
    }
  }

  while (primaryIndex < safePrimarySubHdr.length) {
    mergedHeaders.push({ label: safePrimarySubHdr[primaryIndex] });
    primaryIndex++;
  }

  return mergedHeaders;
};

export const getScheduleDays = (schedule) => {
  return Array.isArray(schedule)
    ? schedule.map((entry) => entry.day.toLowerCase())
    : [];
};

export const getSumAdditional = (incentives, csv) => {
  if (!Array.isArray(incentives) || incentives.length === 0) {
    return {};
  }

  if (!Array.isArray(csv) || csv.length === 0) {
    return {};
  }

  const camelKeys = incentives
    .map((i) => i?.name)
    .filter(Boolean)
    .map(toCamelCase);
  const result = {};

  csv.forEach((row) => {
    const empNo = row.agencyEmployeeNo?.trim();
    if (!empNo) return;

    let total = 0;

    camelKeys.forEach((key) => {
      const rawValue = row[key];
      const value = parseFloat(rawValue);
      if (!isNaN(value)) {
        total += value;
      }
    });

    if (total > 0) {
      result[empNo] = (result[empNo] || 0) + total;
    }
  });

  return result;
};

export const getSumContributions = (contributions, type) => {
  if (!Array.isArray(contributions) || contributions.length === 0) return [];
  const contributionsLists = contributions.map((item) => {
    const { contributions: contributionData = {} } = item;

    let total = 0;

    if (type === "all") {
      total = Object.values(contributionData).reduce(
        (sum, value) => sum + (parseFloat(value) || 0),
        0
      );
    } else {
      total = Object.entries(contributionData)
        .filter(([key]) => key !== "pagibig")
        .reduce((sum, [, value]) => sum + (parseFloat(value) || 0), 0);
    }

    const roundedTotal = parseFloat(total.toFixed(2));

    return {
      id: item.id,
      totalContributions: roundedTotal,
    };
  });

  return Object.fromEntries(
    contributionsLists.map(({ id, totalContributions }) => [
      id,
      totalContributions,
    ])
  );
};
// Always ignore "gsisLR"; optionally ignore more keys
export const sumObjContributions = (contributions, extraExclude = []) => {
  if (!Array.isArray(contributions) || contributions.length === 0) return {};

  // Start with "gsisLR" and add any extra exclusions
  const excludeSet = new Set(["gsisLR"]);
  if (extraExclude) {
    (Array.isArray(extraExclude) ? extraExclude : [extraExclude]).forEach((k) =>
      excludeSet.add(k)
    );
  }

  const result = {};

  contributions.forEach(({ id, contributions: contribs = {} }) => {
    let total = 0;

    for (const [key, value] of Object.entries(contribs)) {
      if (!excludeSet.has(key) && typeof value === "number") {
        total += value;
      }
    }

    result[id] = total;
  });

  return result;
};

export const getSumCsvData = (
  csv,
  headers = "all",
  { exclude = false } = {}
) => {
  if (!Array.isArray(csv) || csv.length === 0) return {};

  return csv.reduce((acc, employee) => {
    const { agencyEmployeeNo, ...fields } = employee;
    if (!agencyEmployeeNo) return acc;

    let targetKeys;

    if (headers === "all") {
      targetKeys = Object.keys(fields).filter((k) => k !== "tax");
    } else if (headers === "tax") {
      targetKeys = ["tax"];
    } else if (Array.isArray(headers)) {
      const hdrs = headers.map(String);
      if (exclude) {
        targetKeys = Object.keys(fields).filter((k) => !hdrs.includes(k));
      } else {
        targetKeys = hdrs;
      }
    } else {
      targetKeys = [String(headers)];
    }

    const sum = targetKeys.reduce(
      (total, key) => total + (parseFloat(fields[key]) || 0),
      0
    );

    acc[agencyEmployeeNo] = (acc[agencyEmployeeNo] || 0) + sum;
    return acc;
  }, {});
};

export const createLookup = (array = [], key, valueKey) => {
  if (!Array.isArray(array)) return {};

  return Object.fromEntries(
    array
      .filter((item) => item && item[key] !== undefined)
      .map((item) => [item[key], item[valueKey]])
  );
};

export const findLabel = (value, options) => {
  return options.find((option) => option.value === value)?.label ?? "";
};

export const _chunkArray = (array, size) => {
  return Array.from({ length: Math.ceil(array.length / size) }, (_, i) =>
    array.slice(i * size, i * size + size)
  );
};

export const _omitKeys = (obj, keys) => {
  return Object.fromEntries(
    Object.entries(obj).filter(([key]) => !keys.includes(key))
  );
};

export const extractDayNumbers = (dateSet, leaveWithoutPay = null) => {
  // Create a combined Set with both dateSet and leaveWithoutPay dates
  const combinedSet = new Set();
  
  if (dateSet && dateSet instanceof Set) {
    dateSet.forEach(date => combinedSet.add(date));
  }
  
  if (leaveWithoutPay && leaveWithoutPay instanceof Set) {
    leaveWithoutPay.forEach(date => combinedSet.add(date));
  }
  
  if (combinedSet.size === 0) return "";

  const days = Array.from(combinedSet)
    .map((dateStr) => {
      const date = new Date(dateStr);
      return date.getDate();
    })
    .sort((a, b) => a - b); // Sort day numbers in ascending order

  return days.join(", ");
};

export const excludeLeaveFromAbsences = (absentDatesByEmp, leaveDatesByEmp = {}, employeeId = null) => {
  const result = {};
 
  if (!absentDatesByEmp) {
    return result;
  }
 
  if (!leaveDatesByEmp || typeof leaveDatesByEmp !== 'object') {
    leaveDatesByEmp = {};
  }
 
  // Handle case when absentDatesByEmp is a Set or Array directly (single employee)
  if (absentDatesByEmp instanceof Set || Array.isArray(absentDatesByEmp)) {
    if (!employeeId) {
      return result;
    }
    
    const leaveDates = leaveDatesByEmp[employeeId] ?? new Set();
    const filteredAbsents = new Set(absentDatesByEmp);

    if (leaveDates) {
      for (const date of leaveDates) {
        filteredAbsents.delete(date);
      }
    }
    result[employeeId] = filteredAbsents;
    return result;
  }
 
  if (typeof absentDatesByEmp !== 'object') {
    return result;
  }
 
  for (const [empId, absentDates] of Object.entries(absentDatesByEmp)) {
    const leaveDates = leaveDatesByEmp[empId] ?? new Set();

    const filteredAbsents = new Set(absentDates);

    if (leaveDates) {
      for (const date of leaveDates) {
        filteredAbsents.delete(date);
      }
    }
    result[empId] = filteredAbsents;
  }
 
  return result;
 };

export const getTotalNetOfMandatory = (
  formatted,
  netOfMandatoryArray = [],
  type,
  valueToGet
) => {
  if (!Array.isArray(netOfMandatoryArray)) return 0;
  const formattedDate = new Date(`${formatted} 1`);
  const formattedYear = formattedDate.getFullYear();

  // console.log("formattedDate", formattedDate);

  return netOfMandatoryArray.reduce((sum, item) => {
    if (!item || typeof item !== "object") return sum;

    const entry = Object.entries(item)[0];
    if (!entry) return sum;

    const [key, value] = entry;

    if (!key.startsWith(type)) return sum;

    const match = key.match(/\(([^)]+)\)/);
    if (!match) return sum;

    const monthYear = match[1]; // e.g., "January - 2025"
    const itemDate = new Date(`${monthYear} 1`);

    // console.log("itemDate", itemDate);

    const itemYear = itemDate.getFullYear();

    if (itemYear === formattedYear) {
      if (
        (type === "Basic Pay" || type === "Augmentation Pay") &&
        (valueToGet === "netOfMandatory" || valueToGet === "monthlyTax") &&
        itemDate < formattedDate
      ) {
        return sum + (parseFloat(value) || 0);
      }
      if (type === "Basic Pay" && valueToGet === "annualTax") {
        if (
          itemDate.getMonth() === formattedDate.getMonth() &&
          itemDate.getFullYear() === formattedDate.getFullYear()
        ) {
          return parseFloat(value) || 0;
        }
      }
      if (type === "Basic Pay" && valueToGet === "annualTaxation") {
        if (monthYear.includes("January")) {
          return parseFloat(value) || 0;
        }
      }
      if (type === "Basic Pay" && valueToGet === "netEarned") {
        if (monthYear.includes("January")) {
          return parseFloat(value) || 0;
        }
      }
      if (type === "Augmentation Pay" && valueToGet === "payCode") {
        // const prevMonthDate = new Date(formattedDate);
        // prevMonthDate.setMonth(prevMonthDate.getMonth() - 1);

        // if (itemDate.getTime() === prevMonthDate.getTime()) {
        //   return parseFloat(value) || 0;
        // }

        if (itemDate < formattedDate) {
          return sum + (parseFloat(value) || 0);
        }
      }
    }

    return sum;
  }, 0);
};

export const getExactNetOfMandatory = (
  formatted,
  netOfMandatoryArray = [],
  type
) => {
  return netOfMandatoryArray.reduce((result, item) => {
    const [key, value] = Object.entries(item)[0];

    // Match key format: "Basic Pay (January - 2025)"
    if (key.startsWith(type) && key.includes(`(${formatted})`)) {
      return parseFloat(value) || 0;
    }

    return result;
  }, 0);
};

export const getTaxableAllowances = (
  allowances = {},
  istaxable,
  allowanceType = ""
) => {
  const result = {};

  Object.entries(allowances).forEach(([empId, allowanceList]) => {
    if (Array.isArray(allowanceList)) {
      if (istaxable === "Yes") {
        const total = allowanceList.reduce((sum, item) => {
          if (item.taxable === "YES") {
            return sum + (parseFloat(item.amount) || 0);
          }
          return sum;
        }, 0);
        result[empId] = total;
      } else if (istaxable === "No") {
        const gratuityItem = allowanceList.find(
          (item) => item.allowance === allowanceType
        );
        result[empId] = gratuityItem ? parseFloat(gratuityItem.amount) || 0 : 0;
      }
    }
  });

  return result;
};

export const getYearOptions = () => {
  const startYear = 2015;
  const currentYear = new Date().getFullYear();
  const years = [];

  for (let year = currentYear; year >= startYear; year--) {
    years.push(year.toString());
  }
  return years;
};

export const getWholeDaysofMonth = (employeeDateRanges) => {
  const result = {};

  for (const [employeeId, ranges] of Object.entries(employeeDateRanges)) {
    const monthDayCounts = [];

    for (const range of ranges) {
      const [startDateStr] = range.split(" - ");
      const [year, month] = startDateStr.split("-").map(Number);

      // Get number of days in that month
      const daysInMonth = new Date(year, month, 0).getDate();
      monthDayCounts.push(daysInMonth);
    }

    result[employeeId] = monthDayCounts;
  }

  return result;
};

export const getMonthNames = (employeeDateRanges) => {
  const result = {};
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  for (const [employeeId, ranges] of Object.entries(employeeDateRanges)) {
    const monthLabels = [];

    for (const range of ranges) {
      const [startDateStr] = range.split(" - ");
      const [year, month] = startDateStr.split("-").map(Number);

      const monthNameWithYear = `${monthNames[month - 1]} ${year}`; // month is 1-based
      monthLabels.push(monthNameWithYear);
    }

    result[employeeId] = monthLabels;
  }

  return result;
};

export const calculateBackPay = (rate, counts) =>
  counts.map((count) => Number(((rate / 22) * count).toFixed(2)));

export const getDatesFromRange = (dateRange) => {
  const selectedDates = [];

  Object.values(dateRange).forEach((ranges) => {
    ranges.forEach((rangeStr) => {
      const [start, end] = rangeStr.split(" - ");
      if (start && end) {
        selectedDates.push({
          startDate: formatDate(start),
          endDate: formatDate(end),
        });
      }
    });
  });

  return selectedDates;
};

export const getRangeInSelectedDate = ({ startDate, endDate }) => {
  const MS_PER_DAY = 1000 * 60 * 60 * 24;
  const start = new Date(startDate);
  const end = new Date(endDate);

  // difference in whole days (inclusive)
  return Math.floor((end - start) / MS_PER_DAY) + 1;
};

export const countDateRanges = (dateRangeObj) => {
  const result = {};

  for (const [employeeId, ranges] of Object.entries(dateRangeObj)) {
    const counts = ranges.map((range) => {
      const [startStr, endStr] = range.split(" - ");
      const startDate = new Date(startStr);
      const endDate = new Date(endStr);

      const diffInMs = endDate - startDate;
      const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24)) + 1;

      return diffInDays;
    });

    result[employeeId] = counts;
  }

  return result;
};

export const getDaysInMonth = ({ startDate } = {}) => {
  if (!startDate) return 0;
  const date = new Date(startDate);
  const year = date.getFullYear();
  const month = date.getMonth();

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  return daysInMonth;
};

export const countAirportDuties = (airportSched, selectedDate, objects) => {
  // Ensure selectedDate is an array of ranges
  const dateRanges = Array.isArray(selectedDate) ? selectedDate : [];
  const validEmpIds = new Set(
    (objects || []).map((emp) => emp?.id).filter(Boolean)
  );

  // Prepare result: { [empId]: [count, ...] }
  const dutyCountByEmployee = {};
  for (const emp of objects || []) {
    if (!emp?.id) continue;
    dutyCountByEmployee[emp.id] = Array(dateRanges.length).fill(0);
  }

  // For each date range, count duties for each employee
  dateRanges.forEach(({ startDate, endDate }, rangeIdx) => {
    if (!startDate || !endDate) return;
    // Build a Set of all dates in this range
    const rangeDates = new Set();
    let cur = new Date(startDate);
    const end = new Date(endDate);
    while (cur <= end) {
      rangeDates.add(cur.toISOString().split("T")[0]);
      cur.setDate(cur.getDate() + 1);
    }
    // For each schedule, count for assigned employees
    for (const sched of airportSched || []) {
      if (!sched.startDate || !sched.endDate) continue;
      const schedStart = new Date(sched.startDate);
      const schedEnd = new Date(sched.endDate);
      for (const rel of sched.assignRelation || []) {
        const empId = rel.employee?.id;
        if (!empId || !validEmpIds.has(empId)) continue;
        let count = 0;
        let d = new Date(schedStart);
        while (d <= schedEnd) {
          const dStr = d.toISOString().split("T")[0];
          if (rangeDates.has(dStr)) count++;
          d.setDate(d.getDate() + 1);
        }
        dutyCountByEmployee[empId][rangeIdx] =
          (dutyCountByEmployee[empId][rangeIdx] || 0) + count;
      }
    }
  });

  return dutyCountByEmployee;
};

export const getInServiceCount = ({
  id,
  actualWorkDays = {},
  absentDateByEmployee = {},
  halfDateByEmployee = {},
}) => {
  const workDays = actualWorkDays[id] || [];
  const absents = absentDateByEmployee[id] || new Set();
  const halfDays = halfDateByEmployee[id] || new Set();

  let count = 0;

  for (const date of workDays) {
    if (absents.has(date)) continue;
    if (halfDays.has(date)) count += 0.5;
    else count += 1;
  }

  return count;
};

export const getActualAttendanceDates = (
  workingDates = [],
  totalAbsences = []
) => {
  if (!Array.isArray(workingDates) || !Array.isArray(totalAbsences)) return [];

  return workingDates.map((datesArr, index) => {
    const absences = totalAbsences[index] || 0;

    if (!Array.isArray(datesArr)) return 0;

    const attendanceCount = Math.max(0, datesArr.length - absences);
    return attendanceCount;
  });
};

export const calculateTotalFromMins = ({ mins, twentyFourSeven }, apRate) => {
  if (!mins || mins <= 0) return 0;

  let hoursPerDay = 12;

  if (typeof twentyFourSeven === "string") {
    const match = twentyFourSeven.match(/([\d.]+)\s*Hours?/i);
    if (match) {
      hoursPerDay = parseFloat(match[1]);
    }
  }

  const dailyRate = apRate / 22 / hoursPerDay;
  const perMinuteRate = dailyRate / 60;
  const value = perMinuteRate * mins;

  // truncate to 2 decimal places instead of rounding
  return value;
};

// utils/helpers.js or in the same file
export const filterBySuspensionDate = (objects, datePicked) => {
  return objects.filter((emp) => {
    if (!emp.dateOfSuspension) return true; // include if no suspension

    const endDate = new Date(datePicked?.endDate);
    const suspensionDate = new Date(emp.dateOfSuspension);

    // Include only if endDate is before suspensionDate
    return endDate <= suspensionDate;
  });
};

export const normalizeId = (value) => {
  if (value == null) return "";

  const str = String(value).trim();

  // If purely numeric, normalize as number-string (removes leading zeros)
  if (/^\d+$/.test(str)) {
    return String(Number(str));
  }

  // Otherwise keep alphanumeric as-is
  return str;
};

export const extractYearMonth = (values = [], type) => {
  if (!Array.isArray(values)) return [];

  return values.map((value) => {
    if (typeof value !== "string") return null;

    const match = value.match(/^([A-Za-z]+)\s+(\d{4})$/);
    if (!match) return null;

    const [, monthName, yearStr] = match;
    const year = Number(yearStr);

    if (type === "year") {
      return Number.isNaN(year) ? null : year;
    }

    if (type === "month") {
      return monthName;
    }

    return null;
  }).filter(v => v !== null);
}

export const formatAbsenceDates = (absencesDate, leaveWithoutPaySetArray = []) => {
  if (!Array.isArray(absencesDate)) return [];

  return absencesDate.map((dateSet, idx) => {
    const leaveSet =
      Array.isArray(leaveWithoutPaySetArray) && leaveWithoutPaySetArray[idx] instanceof Set
        ? leaveWithoutPaySetArray[idx]
        : null;

    const combined = new Set();
    if (dateSet instanceof Set) for (const d of dateSet) combined.add(d);
    if (leaveSet) for (const d of leaveSet) combined.add(d);

    if (combined.size === 0) return "-";

    const times = [];
    for (const dateStr of combined) {
      const ts = Date.parse(dateStr);
      if (Number.isFinite(ts)) times.push(ts);
    }

    times.sort((a, b) => a - b);

    return times.map((ts) => new Date(ts).getDate()).join(", ");
  });
};


//new

export const  _hasLength = (value) => {
  if (!value) return false;

  if (typeof value === "object") {
    return Object.keys(value).length > 0;
  }

  return false;
}

export const _createEmptyFilters = (excludeKeys = []) => {
  const keys = [...new Set(Object.values(FIELD_FILTER_MAP))];
  return keys.reduce((acc, key) => {
    // Skip keys that should be preserved
    if (excludeKeys.includes(key)) {
      return acc;
    }
    acc[key] = {};
    return acc;
  }, {});
};

export const _getSetupsHolder = async ({payrollTypes, employmentType, payItems}, holderSetup) => {

  const holder = await findObjectUseCase().execute("payroll_setups", {
    keys: ["employmentTypes", "payItems", "payrollTypes"],
    where: {
      "payrollTypes.id": payrollTypes?.value?.id,
      "employmentTypes.id": employmentType?.value?.id,
      "payItems.id": payItems?.value?.id
    }
  });

   return holderSetup[holder[0]?.id];
}

